import Accordion from '@/common/components/Accordion'
import Box from '@/common/components/Box'
import MaintenanceObjective from '@/common/components/MaintenanceObjective'
import ResumeItem from '@/common/components/ResumeItem'
import ReviewDescription from '@/common/components/ReviewDescription'
import { formatCurrency, formatDateTime } from '@/common/utils/formatters'
import DiagnosticEntryObjectiveActivities from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Content/DiagnosticEntryCompleted/DiagnosticEntryObjectiveActivities'
import DiagnosticEntryObjectivePieces from '@/features/maintenanceWorkflow/pages/Show/components/tabs/ForwardingsTabs/Sections/DirectiveEntry/Content/DiagnosticEntryCompleted/DiagnosticEntryObjectivePieces'
import {
  MaintenanceForwardingDirectiveEntryDiagnosticEntryData,
  MaintenanceForwardingDirectiveEntryStatus,
} from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { useForwardingTabContext } from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useForwardingTabContext'
import useGetDirectiveEntryObjectivesSummary from '@/features/maintenanceWorkflow/pages/Show/hooks/forwardings/useGetDirectiveEntryObjectivesSummary'

interface DirectiveEntryDiagnosticEntryCompletedProps {
  lastReview: MaintenanceForwardingDirectiveEntryDiagnosticEntryData['last_review'] | undefined
  processStartedAt: string | null | undefined
  completedAt: string | null | undefined
  technicalResponse: string | undefined
  deadLineToCompleteInDays: number | null | undefined
  totalValue: string
}

const DirectiveEntryDiagnosticEntryCompleted = ({
  lastReview,
  processStartedAt,
  completedAt,
  technicalResponse,
  deadLineToCompleteInDays,
  totalValue,
}: DirectiveEntryDiagnosticEntryCompletedProps) => {
  const { forwarding, forwardingUpdatedAt } = useForwardingTabContext()

  const directiveEntryObjectivesSummary = useGetDirectiveEntryObjectivesSummary()

  const isDiagnosticEntryDisapproved = lastReview?.state === 'disapproved'

  return (
    <Box className="flex flex-col gap-4 p-4">
      <div className="flex flex-col gap-1.5">
        {isDiagnosticEntryDisapproved && (
          <ReviewDescription
            description={lastReview?.description}
            showAlert={forwarding?.directive_entry?.status === MaintenanceForwardingDirectiveEntryStatus.InProgress}
          />
        )}

        {(processStartedAt || completedAt) && (
          <div className="flex gap-3">
            {processStartedAt && (
              <ResumeItem
                label="Diagnóstico e orçamento enviado"
                value={formatDateTime(new Date(processStartedAt))}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}

            {completedAt && (
              <ResumeItem
                label="Aprovado em"
                value={formatDateTime(new Date(completedAt))}
                className="text-xs"
                valueClassName="font-bold text-sm"
              />
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        {technicalResponse && (
          <ResumeItem
            label="Parecer técnico"
            value={technicalResponse}
            className="text-xs"
            valueClassName="font-bold text-sm"
          />
        )}

        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {deadLineToCompleteInDays && (
            <ResumeItem
              label="Prazo para finalização da execução"
              value={`${deadLineToCompleteInDays} dias`}
              className="text-xs"
              valueClassName="font-bold text-sm"
            />
          )}

          <ResumeItem
            label="Total orçado em peças"
            value={`${directiveEntryObjectivesSummary.piecesCountDisplay} • ${formatCurrency(directiveEntryObjectivesSummary.piecesTotalValue || 0)}`}
            className="text-xs"
            valueClassName="font-bold text-sm"
          />

          <ResumeItem
            label="Total orçado em serviços"
            value={`${directiveEntryObjectivesSummary.activitiesCountDisplay} • ${formatCurrency(directiveEntryObjectivesSummary.activitiesTotalValue || 0)}`}
            className="text-xs"
            valueClassName="font-bold text-sm"
          />

          <ResumeItem
            label="Valor total do orçamento"
            value={formatCurrency(parseFloat(totalValue) || 0)}
            className="text-xs"
            valueClassName="font-bold text-sm"
          />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <p className="text-base font-bold">Orçamento</p>

        {forwarding?.directive_entry?.objectives?.map((objective) => {
          const piecesCount = objective.diagnostic_pieces?.length || 0
          const activitiesCount = objective.diagnostic_activities?.length || 0

          const piecesTotalValue =
            objective.diagnostic_pieces?.reduce((prev, curr) => prev + Number(curr.total_value), 0) || 0
          const activitiesTotalValue =
            objective.diagnostic_activities?.reduce((prev, curr) => prev + Number(curr.total_value), 0) || 0

          const piecesCountDisplay =
            piecesCount === 0 ? 'Nenhuma peça' : piecesCount === 1 ? '1 Peça' : `${piecesCount} Peças`
          const activitiesCountDisplay =
            activitiesCount === 0 ? 'Nenhum serviço' : activitiesCount === 1 ? '1 Serviço' : `${activitiesCount} Serviços`

          return (
            <Accordion
              key={objective.id}
              className="p-0 rounded-lg"
              triggerClassName="p-4"
              bodyClassName="p-4 pt-0"
              head={
                <div className="flex flex-1 items-center justify-between gap-4 min-w-0 pr-2">
                  <div className="min-w-0">
                    <MaintenanceObjective
                      systemDisplayName={objective.system.display_name}
                      actionDisplayName={objective.action.display_name}
                      partDisplayName={objective.part.display_name}
                      actionDescription={objective.action.description}
                    />
                  </div>

                  <div className="flex shrink-0 flex-col items-end gap-0.5 text-xs">
                    <div className="flex items-center gap-0.5">
                      <span>{piecesCountDisplay}</span>

                      <span>•</span>

                      <span className="text-valid">{formatCurrency(piecesTotalValue)}</span>
                    </div>

                    <div className="flex items-center gap-0.5">
                      <span>{activitiesCountDisplay}</span>

                      <span>•</span>

                      <span className="text-valid">{formatCurrency(activitiesTotalValue)}</span>
                    </div>
                  </div>
                </div>
              }
              body={
                <div className="flex flex-col gap-3">
                  <DiagnosticEntryObjectivePieces objective={objective} dataUpdatedAt={forwardingUpdatedAt} />

                  <DiagnosticEntryObjectiveActivities objective={objective} dataUpdatedAt={forwardingUpdatedAt} />
                </div>
              }
            />
          )
        })}
      </div>
    </Box>
  )
}

export default DirectiveEntryDiagnosticEntryCompleted
